import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Button, Box } from "@mui/material";
import Pagination from "components/Pagination/Pagination";
import FilterSidebar from "components/FilterSidebar/FilterSidebar";
import SearchBar from "../SearchBar/SearchBar";
import formatField from "../../utils/formatFields";
import { useDispatch, useSelector } from "react-redux";
import { fetchDataRequest } from "../../redux/api/apiSlice";
import {
  resetFiltersAndSearch,
  searchQueryChanged,
  setSelectedFilters,
} from "../../redux/searchAndFilter/searchAndFilterSlice";
import styles from "../Datasets/Datasets.module.css";

const DATASET_NAME = "RESILIAGE T1.1 Policies and standards";

const PoliciesStandards = () => {
  const dispatch = useDispatch();
  const { datasets, loading, error } = useSelector((state) => state.api); // Extract dataset, loading, and error states from Redux store
  const policies = datasets?.[DATASET_NAME] || []; //Get data for the specific dataset
  const { selectedFilters, searchQuery } = useSelector(
    (state) => state.searchFilter
  );
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch dataset when the component is mounted or lessons learned data changes
  useEffect(() => {
    if (!policies.length) {
      dispatch(fetchDataRequest(DATASET_NAME));
    }
  }, [dispatch, policies]);

  // Handle changes to filters (update Redux state with selected filters)
  const handleFilterChange = (category, value, dataset) => {
    dispatch(setSelectedFilters({ category, value, dataset }));
  };

  // Handle changes to the search query (update Redux state and reset pagination to first page)
  const handleSearchChange = (event) => {
    const newQuery = event.target.value;
    dispatch(searchQueryChanged(newQuery));
    setPage(1);
  };

  // Filter dataset based on selected filters and search query
  const filteredPolicies = policies.filter((policy) => {
    const datasetFilters = selectedFilters[DATASET_NAME];
    if (!datasetFilters) return true;
    const matchesSelectedFilters = datasetFilters
      ? Object.keys(datasetFilters).every((category) => {
          if (!datasetFilters[category]?.length) return true; // Skip if no filters are selected for the category
          const projectValue = policy[category] || "";
          return datasetFilters[category].some((filterValue) =>
            projectValue.includes(filterValue)
          );
        })
      : true;

    const matchesSearchQuery = policy["Name"]
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    return matchesSelectedFilters && matchesSearchQuery;
  });

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPolicies = filteredPolicies.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleResetFilters = (dataset) => {
    dispatch(resetFiltersAndSearch(dataset));
  };

  return (
    <Box className={styles.mainContainer}>
      <FilterSidebar
        dataset={DATASET_NAME}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      <Box className={styles.contentArea}>
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />

        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : (
          paginatedPolicies.map((policy, index) => (
            <Card key={index} variant="outlined" className={styles.contentCard}>
              <CardContent>
                <Typography variant="h6">{policy.Name}</Typography>
                <Typography>
                  <strong>Authority:</strong> {formatField(policy["Authority"])}
                </Typography>
                <Typography>
                  <strong>Chief policy relevancy:</strong>{" "}
                  {formatField(policy["Chief policy relevancy"])}
                </Typography>
                <Typography>
                  <strong>Chief DMC stage:</strong>{" "}
                  {formatField(policy["Chief DMC stage"])}
                </Typography>
                <Typography>
                  <strong>Level:</strong> {policy.Level}
                </Typography>
                <Typography>
                  <strong>Type:</strong> {policy.Type}
                </Typography>
                <Typography>
                  <strong>Binding:</strong> {policy["Binding?"]}
                </Typography>
                <Typography>
                  <strong>Chief audience:</strong>{" "}
                  {formatField(policy["Chief audience"])}
                </Typography>
                <Button href={policy.link[0]} target="_blank" color="secondary">
                  More info
                </Button>
              </CardContent>
            </Card>
          ))
        )}

        <Pagination
          count={Math.ceil(filteredPolicies.length / itemsPerPage)}
          page={page}
          onPageChange={handlePageChange}
        />
      </Box>
    </Box>
  );
};

export default PoliciesStandards;
